import React, {
  useCallback,
  useRef,
  useState,
  useEffect,
  useMemo,
} from 'react';
import moment from 'moment';
import styled from 'styled-components';

import {
  message,
  theme,
  Tabs,
  TabPane,
  ListingEntry,
  ListingMapView,
} from 'components';
import { Country, IAppointmentConfig, JsonObject } from '@bb/domain/dist/types';
import { FEEDBACK_TYPES } from '@/constants';
import classNames from 'classnames';

import { featureEnabled } from 'utils';
import { useCovidSign } from 'hooks';
import API from 'api';
import axios from 'axios';

import { PermissionsType } from 'api/AppExternalController';

import { Feature } from '@bb/automaton';

import Covid19UploadedPdfModal from '@app/covid19/Covid19UploadedPdfModal';
import { CovidAppointmentType } from '@app/covid19/COVID19AckTemplate';
import CovidAckTemplateModal from '@app/covid19/CovidAckTemplateModal';
import Covid19SignedAckModal from '@app/covid19/Covid19SignedAckModal';
import { useSendingReminder } from '@app/covid19/hooks';

import { ExternalFeedback } from 'api/FeedbackController/getExternalFeedback';
import {
  AccessDetailsInfo,
  Actions,
  AutoActions,
  CovidBanner,
  PropertyUpdateBanner,
  DetailsSection,
  FeedbackSection,
  HistorySection,
  LogsSection,
  StatusBanner,
  DetailRow,
  Section,
} from './components';
import {
  ShowingDetailsAppointmentType,
  ShowingDetailsListingType,
  Covid19StateType,
  ShowingDetailsPageUserType,
} from './types';

import AgentSection from './components/AgentSection';
import { CoopCancelReasonType } from '../../../app/DashboardComponents/CoopCancelAppointmentComponent';
import CovidSectionLA from './components/CovidSectionLA';
import PreviousTimeInfo from './components/PreviousTimeInfo';

type ShowingDetailsPageProps = {
  isListingAgent?: boolean;
  auth: {
    id?: string;
    org: { country: Country };
    hasFeature: (feature: Feature) => boolean;
  };
  userType: ShowingDetailsPageUserType;
  appointment: ShowingDetailsAppointmentType;
  setAppointment?: React.Dispatch<
    React.SetStateAction<ShowingDetailsAppointmentType>
  >;
  fetchAppointment: () => Promise<void>;
  listing: ShowingDetailsListingType;
  listingAppointmentConfig?: JsonObject<IAppointmentConfig>;
  feedbackConfig?: ExternalFeedback;
  setFeedbackConfig: (update: ExternalFeedback) => void;
  fetchFeedback: () => Promise<void>;
  lockboxPhoto: string;
  offerDetails: any;
  tourDetails?: any;
  signatureType: any;
  dashboardInfo?: { dashboardId: string; retsId: string };
  appointmentPermissions?: PermissionsType;
  queryParams?: Record<string, string>;
  tourFunctions?: {
    rescheduleFromTour: () => void;
    cancelFromTour: (
      reason: CoopCancelReasonType,
      details: string,
      onConfirm?: () => void,
    ) => Promise<void>;
    acceptSuggestionFromTour: () => Promise<void>;
    denySuggestionFromTour: () => void;
    closeAppointmentModal: () => void;
  };
  fromTourPage?: boolean;
  isCustomContact?: boolean;
  isExternal?: boolean;
};

const ShowingDetailsPage: React.FC<ShowingDetailsPageProps> = ({
  isListingAgent,
  isCustomContact,
  auth,
  userType,
  appointment,
  fetchAppointment,
  listing,
  listingAppointmentConfig,
  feedbackConfig,
  setFeedbackConfig,
  fetchFeedback,
  lockboxPhoto,
  offerDetails,
  tourDetails,
  signatureType,
  dashboardInfo,
  appointmentPermissions,
  queryParams,
  tourFunctions,
  fromTourPage,
  isExternal,
}) => {
  const [currentTab, setCurrentTab] = React.useState<
    'details' | 'history' | 'logs'
  >('details');
  const handleTabChange = (key: string) => {
    setCurrentTab(key as typeof currentTab);
  };

  const {
    selectedCovidAppt,
    setSelectedCovidAppt,
    selectedCovidTemplate,
    resetCovidStates,
    handleCovidSign,
    selectedCovidSignSettings,
  } = useCovidSign(
    dashboardInfo ? { ...dashboardInfo, country: auth.org.country } : undefined,
  );
  const [sendingCovidReminder, setSendingCovidReminder] = useState(false);
  const [showSignedAck, setShowSignedAck] = useState(false);
  const [autoAction, setAutoAction] = useState<AutoActions>(undefined);
  const [validatedAck, setValidatedAck] = React.useState(
    !!appointment.covid19Signed,
  );
  const feedbackRef = useRef<HTMLDivElement>(null);
  const scrollToFeedback = () => {
    feedbackRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const { sendingReminder, covid19ReminderSent, sendReminder } =
    useSendingReminder({ appointment });

  const [PDFAckFile, setPDFAckFile] = React.useState('');
  const [showUploadedPDF, setShowUploadedPDF] = React.useState(false);
  const openModal = (type: string) => {
    if (type === 'sign') setShowSignedAck(true);
    else if (type === 'pdf') setShowUploadedPDF(true);
  };
  const closeModal = (type: string) => {
    if (type === 'sign') setShowSignedAck(false);
    else if (type === 'pdf') setShowUploadedPDF(false);
  };

  const [laCovidOverridden, setLACovidOverridden] = useState(false);

  const showPropertyBanner = [
    'new',
    'attempted',
    'time_change',
    'suggested_time_change',
    'confirm',
  ].includes(appointment.status);

  useEffect(() => {
    if (queryParams) {
      if (queryParams.sign === 'true') {
        setSelectedCovidAppt(appointment as unknown as CovidAppointmentType);
      } else if (queryParams.status === 'cancel') {
        setAutoAction('cancel');
      } else if (queryParams.status === 'deny') {
        setAutoAction('deny');
      } else if (queryParams.status === 'autoconfirm') {
        setAutoAction('autoconfirm');
      } else if (queryParams.status === 'confirm') {
        setAutoAction('confirm');
      }
      if (queryParams.submitFeedback === 'true') {
        scrollToFeedback();
      }
    }
  }, [queryParams?.sign, queryParams?.status, queryParams?.submitFeedback]);

  const sendReminderAction = React.useMemo(
    () => (
      <button
        style={{ color: theme.blue6 }}
        data-cy="reminder-action-button"
        onClick={async () => {
          try {
            setSendingCovidReminder(true);
            await API.POST.sendCovidReminder(appointment.id);
            message.success('COVID-19 reminder sent');
          } catch (err) {
            message.error('Error sending COVID-19 reminder');
          } finally {
            setSendingCovidReminder(false);
          }
        }}
      >
        <i
          className={classNames('fa', {
            'fa-spinner': sendingCovidReminder,
            'fa-spin': sendingCovidReminder,
          })}
        />{' '}
        Send Reminder
      </button>
    ),
    [appointment.id],
  );

  const signWaiverAction: JSX.Element = (
    <button
      data-cy="sign-waiver-action-button"
      style={{ color: theme.blue6 }}
      onClick={() =>
        setSelectedCovidAppt(appointment as unknown as CovidAppointmentType)
      }
    >
      Sign Waiver
    </button>
  );

  const viewWaiverAction: JSX.Element = (
    <button
      data-cy="view-waiver-action-button"
      style={{ color: theme.blue6 }}
      onClick={() => {
        if (isListingAgent && PDFAckFile) {
          openModal('pdf');
        } else {
          setSelectedCovidAppt(appointment as unknown as CovidAppointmentType);
          setShowSignedAck(true);
        }
      }}
    >
      View COVID-19 waiver
    </button>
  );

  React.useEffect(() => {
    const getAppointmentSignature = async () => {
      const url = `/covid19/appointment/${appointment.id}/settings`;

      const { data } = await axios.get(url);
      if (data) {
        if (data?.PDFAckFile) setPDFAckFile(data.PDFAckFile);
      }
    };

    if (userType === 'agent' && appointment.covid19RequestedAck)
      getAppointmentSignature();
  }, [appointment.covid19RequestedAck, validatedAck, userType]);

  const getCovid19State = useCallback(() => {
    const { covid19RequestedAck, covid19Signed, covid19Overridden } =
      appointment;

    const covid19State: Covid19StateType = { status: 'disabled' };

    if (covid19RequestedAck && !covid19Signed) {
      covid19State.status = 'signatureRequired';
      if (['coop', 'rets'].includes(userType)) {
        covid19State.action = signWaiverAction;
      }
      if (userType === 'agent') {
        covid19State.action = isListingAgent ? (
          <CovidSectionLA
            appointment={appointment}
            setValidatedAck={setValidatedAck}
            validatedAck={validatedAck}
            setLACovidOverridden={setLACovidOverridden}
            sendReminder={sendReminder}
            covid19ReminderSent={covid19ReminderSent}
            sendingReminder={sendingReminder}
          />
        ) : (
          sendReminderAction
        );
      }
    }
    if ((covid19Signed && covid19Overridden) || laCovidOverridden) {
      covid19State.status = 'overridden';
    }
    if (covid19Signed && !covid19Overridden && signatureType === 'pdf') {
      covid19State.status = 'uploaded';
    }

    if (isListingAgent && !laCovidOverridden && PDFAckFile) {
      covid19State.status = 'uploadedLA';
      covid19State.action = viewWaiverAction;
    }
    if (covid19Signed && !covid19Overridden && signatureType === 'template') {
      covid19State.status = 'signed';
      covid19State.action = viewWaiverAction;
    }
    return covid19State;
  }, [
    userType,
    appointment,
    signatureType,
    isListingAgent,
    validatedAck,
    setValidatedAck,
    laCovidOverridden,
    PDFAckFile,
    covid19ReminderSent,
  ]);

  const isPassed = useMemo(
    () => moment(appointment?.timeEnd) < moment(),
    [appointment?.timeEnd],
  );

  const showFeedback = useMemo(() => {
    const { type, status, passed } = appointment;
    const isAcceptingFeedback = FEEDBACK_TYPES.includes(type);
    const isConfirmedAndInPast = status === 'confirm' && passed;
    return (
      isAcceptingFeedback &&
      isConfirmedAndInPast &&
      userType !== 'rets' &&
      userType !== 'client'
    );
  }, [appointment, userType]);

  const dashboardId = dashboardInfo?.dashboardId || dashboardInfo?.retsId;
  const getDetailsSection = () => {
    const hasShowingNotesPhase1Feature = featureEnabled(
      'showingNotesFeature',
      1,
    );

    return (
      <>
        <DetailsSection
          hasFeature={auth.hasFeature}
          isListingAgent={isListingAgent}
          userType={userType}
          appointment={appointment}
          listing={listing}
          offerDetails={offerDetails}
          tourDetails={tourDetails}
          covid19State={getCovid19State()}
          listingAppointmentConfig={listingAppointmentConfig}
          feedbackSubmitted={Boolean(appointment.feedbackCollected)}
          showFeedback={showFeedback}
          scrollToFeedback={scrollToFeedback}
          dashboardId={dashboardId}
          queryParams={queryParams}
          fromTourPage={fromTourPage}
        />
        {(userType !== 'client' || isCustomContact) && (
          <>
            {hasShowingNotesPhase1Feature && Boolean(appointment.note) && (
              <Section title="Agent note:" data-cy="details-note">
                <DetailRow icon="antdFileTextFilled" data-cy="access-info">
                  {appointment.note}
                </DetailRow>
              </Section>
            )}
            <AccessDetailsInfo
              appointment={appointment}
              lockboxPhoto={lockboxPhoto}
            />
          </>
        )}
        <AgentSection
          dashboardId={dashboardId}
          appointmentCoop={appointment.coop}
          reps={listing.reps}
          org={listing.org}
          listingId={listing.id}
          isExternal={isExternal}
        />
        {showFeedback && (
          <div ref={feedbackRef}>
            <FeedbackSection
              auth={auth}
              dashboardId={dashboardId}
              userType={userType}
              appointment={appointment}
              // @ts-expect-error: external feedback is not typed correctly
              feedbackConfig={feedbackConfig!}
              fetchFeedback={fetchFeedback}
              fetchAppointment={fetchAppointment}
              // @ts-expect-error: external feedback is not typed correctly
              setFeedbackConfig={setFeedbackConfig}
            />
          </div>
        )}
      </>
    );
  };

  // Find latest 'history' entry on the appointment with 'cancel' status, grab the 'reason' field
  const getReason = () => {
    const actionDetails = [...appointment.history]
      .reverse()
      .find(
        ({ status }) => status === 'cancel' || status === 'deny',
      )?.actionDetails;
    if (!actionDetails || typeof actionDetails === 'string') return undefined;
    return actionDetails.reason || actionDetails.message;
  };

  return (
    <Layout>
      {showPropertyBanner && !dashboardInfo && (
        <PropertyUpdateBanner listingId={listing.id} />
      )}
      <ListingMapView
        coordinates={listing.coords?.coordinates as [number, number]}
      />
      <BannerContainer>
        {appointment.covid19RequestedAck && !appointment.covid19Signed && (
          <CovidBanner userType={userType} covid19State={getCovid19State()} />
        )}
        <StatusBanner
          status={appointment.status}
          userType={
            // rets/coop => coop, client/agent => agent
            userType === 'rets'
              ? 'coop'
              : userType === 'client'
                ? 'agent'
                : userType
          }
          isPassed={isPassed}
          reason={getReason()}
          listingContactTimeChange={appointment.listingContactTimeChange}
        />
      </BannerContainer>
      <ListingEntry
        listingId={listing.id}
        dashboardId={dashboardInfo?.dashboardId}
        retsId={dashboardInfo?.retsId}
        time={appointment.time}
        timeEnd={appointment.timeEnd}
        timezone={listing.timezone}
        showTimezone
        line1={listing.line1}
        line2={listing.line2}
        thumbnail={listing.thumbnail}
        type={'showing'}
      />
      {userType !== 'rets' && (
        <Actions
          agent={userType}
          appointment={appointment}
          thumbnail={listing.thumbnail}
          updateAppointment={fetchAppointment}
          isPassed={isPassed}
          feedbackSubmitted={Boolean(appointment.feedbackCollected)}
          showFeedback={showFeedback}
          dashboardId={dashboardId}
          scrollToFeedback={scrollToFeedback}
          autoAction={autoAction}
          tourFunctions={tourFunctions}
          appointmentPermissions={appointmentPermissions}
        />
      )}
      {['time_change', 'suggested_time_change'].includes(appointment.status) &&
        appointment.originalTime &&
        appointment.originalTimeEnd && (
          <PreviousTimeInfo
            originalTime={appointment.originalTime}
            originalTimeEnd={appointment.originalTimeEnd}
            timezone={listing.timezone}
          />
        )}
      {listing.repsObjectIds.includes(auth?.id) && !dashboardInfo ? (
        <StyledTabs
          activeKey={currentTab}
          onChange={handleTabChange}
          data-cy="showing-details-tab"
        >
          <StyledTabPane tab="Details" key="details">
            {getDetailsSection()}
          </StyledTabPane>
          <StyledTabPane tab="History" key="history">
            <HistorySection
              appointment={appointment}
              listingAppointmentConfig={listingAppointmentConfig}
              timezone={listing.timezone}
            />
          </StyledTabPane>
          <StyledTabPane tab="Logs" key="logs">
            <LogsSection
              appointment={appointment}
              timezone={listing.timezone}
            />
          </StyledTabPane>
        </StyledTabs>
      ) : (
        getDetailsSection()
      )}

      <CovidAckTemplateModal
        ackTemplate={selectedCovidTemplate}
        // @ts-expect-error
        appointment={selectedCovidAppt}
        country={auth.org.country}
        onCancel={resetCovidStates}
        onSubmit={signature =>
          handleCovidSign(signature, async success => {
            if (success) {
              await fetchAppointment();
            }
          })
        }
        visible={Boolean(selectedCovidAppt && selectedCovidTemplate)}
      />
      <Covid19SignedAckModal
        appointment={appointment}
        appointmentSignSettings={selectedCovidSignSettings}
        country={auth.org.country}
        onCancel={() => {
          resetCovidStates();
          setShowSignedAck(false);
        }}
        visible={showSignedAck}
      />

      {/* view uploaded pdf modal */}
      <Covid19UploadedPdfModal
        onCancel={() => closeModal('pdf')}
        PDFAckFile={PDFAckFile}
        visible={showUploadedPDF}
      />
    </Layout>
  );
};

export default ShowingDetailsPage;

const Layout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  margin: auto;
  padding: 20px;
  margin-bottom: 50px;
  width: 90%;
  background: white;
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin: 0;
  }
  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${theme.blue6};
  }
`;

const StyledTabPane = styled(TabPane)`
  height: 100%;
`;

const BannerContainer = styled.div`
  display: flex;
  flex-flow: column;
  gap: 16px;
  margin-bottom: 16px;
`;
